/**
 * @fileoverview Axios request service: configures the client, injects auth
 * headers, and normalizes success/error/timeout/cancellation handling.
 * @exports request thunk plus interceptor and error-handling helpers
 * @author {Cognizant Technology Solutions}
 */
import Axios from 'axios';
import env from '../env.json';
import {
  setCancellableSpinner, updateErrorText, pushSnackbarContent,
  setRequestError, setSessionTimeout
} from '../features/dialogStates/dialogStatesSlice';
import {
  getAccessToken as getAuthAccessToken,
  getIdToken as getAuthIdToken
  // renewAccessToken as authRenewAccessToken
} from './authService';
import messageText from '../constants/messageText';
import appConfig from '../appConfig';
import { getEndpointURL } from '../helpers/common';

export const axios = Axios.create({
  timeout: appConfig.apiTimeoutDuration,
  headers: {
    'Ocp-Apim-Subscription-Key': env.SUBSCRIPTION_KEY
  }
});

// Holds the active cancel-token source and count of pending cancellable calls.
export const cancellableRequestInstance = {
  source: null,
  pendingCancellableRequests: 0
};

/**
 * Cancels the in-flight cancellable request, if any.
 * @returns {Function} A handler that triggers cancellation
 */
export const cancelRequest = () => () => {
  if (cancellableRequestInstance.source && typeof cancellableRequestInstance.source.cancel === 'function') {
    cancellableRequestInstance.source.cancel('Cancelled by user');
  }
};

/**
 * Extracts the raw access token string from an access token object.
 * @param {Object} accessTokenObj The access token object
 * @returns {?string} The token string, or null when unavailable
 */
function getAccessTokenString(accessTokenObj) {
  if (!accessTokenObj) return null;
  return accessTokenObj.value ?? accessTokenObj.accessToken ?? null;
}

/**
 * Returns the claims object from a token, or an empty object.
 * @param {Object} tokenObj The token object
 * @returns {Object} The token claims
 */
function getTokenClaims(tokenObj) {
  return tokenObj?.claims ?? {};
}

/**
 * Axios request interceptor: injects the subscription key and a fresh bearer
 * JWT (falling back to localStorage) plus user headers, or cancels on expiry.
 * @param {Object} config The outgoing Axios request config
 * @returns {Promise<Object>} The config with auth headers applied
 */
export const defaultInterceptor = async (config) => {
  const modifiedConfig = { ...config };
  let bearerJWT = '';
  let legacyUsername = '';
  let email = '';
  try {
    const authToken = await getAuthAccessToken();
    const idToken = await getAuthIdToken();
    if (authToken) {
      bearerJWT = getAccessTokenString(authToken);
      const claims = getTokenClaims(idToken || authToken);
      email = claims.email ?? '';
      legacyUsername = claims.legacy_username ?? '';
    }
    if (!bearerJWT) {
      const token = localStorage.getItem('okta-token-storage');
      const parsedToken = token ? JSON.parse(token) : null;
      if (parsedToken?.accessToken) {
        bearerJWT = getAccessTokenString(parsedToken.accessToken);
      }
      if (parsedToken?.idToken?.claims) {
        email = parsedToken.idToken.claims.email ?? email;
        legacyUsername = parsedToken.idToken.claims.legacy_username ?? legacyUsername;
      }
    }
  } catch (error) {
    console.log('parsing okta token failed', error);
  }

  if (bearerJWT === '') {
    throw new Axios.Cancel('Session Expired');
  }

  modifiedConfig.headers['Ocp-Apim-Subscription-Key'] = env.SUBSCRIPTION_KEY;
  modifiedConfig.headers['bearer-jwt'] = bearerJWT;
  modifiedConfig.headers.legacy_username = legacyUsername;
  modifiedConfig.headers.username = email;
  modifiedConfig.headers.emailid = email;

  return modifiedConfig;
};

axios.interceptors.request.use(defaultInterceptor);

// axios.interceptors.response.use(
//   (response) => response,
//   async (error) => {
//     const originalRequest = error.config;

//     if (
//       error.response?.status === 401
//       && !originalRequest._retriedDueTo401
//       && originalRequest
//     ) {
//       originalRequest._retriedDueTo401 = true;
//       const newToken = await authRenewAccessToken();
//       if (newToken) {
//         return axios(originalRequest);
//       }
//     }

//     return Promise.reject(error);
//   }
// );

/**
 * Dispatches the configured Axios call for the given HTTP method (or file
 * upload), attaching params, headers, and cancel token as needed.
 * @param {Object} options Request url, method, data, headers, and source
 * @returns {Promise<Object>} The Axios response promise
 */
const fetch = (options) => {
  const {
    url, method, data, responseType, source, headers = {}
  } = options;
  switch (method.toLowerCase()) {
    case 'get': {
      const config = { params: data };
      if (responseType) {
        config.responseType = responseType;
      }
      return axios({
        url,
        method,
        headers,
        config,
        cancelToken: source ? source.token : undefined,
        responseType
      });
    }
    case 'post':
      return axios({
        url,
        headers,
        method,
        cancelToken: source ? source.token : undefined,
        data
      });
    case 'delete':
    case 'put':
      return axios({
        url,
        headers,
        method,
        data
      });
    case 'fileupload':
      return axios({
        url,
        method: 'POST',
        data,
        headers: {
          'Content-Type': 'multipart/form-data',
          Accept: 'application/json',
          type: 'formData'
        }
      });
    default:
      return axios(options);
  }
};

/**
 * Resolves the human-readable message from an API response payload.
 * @param {string|Object} data The response data
 * @param {string} statusText Fallback status text
 * @returns {string} The resolved message
 */
export function getMessageFromApi(data, statusText) {
  return (typeof data === 'string')
    ? data : (typeof data === 'object' && data?.message) || statusText;
}

/**
 * Handles a timed-out or aborted request: shows a snackbar for timeouts, or
 * records a generic internal error otherwise.
 * @param {Object} error The Axios error
 * @param {number} statusCode The current status code
 * @param {string} title The current error title
 * @param {string} desc The current error description
 * @param {string} timeoutErrorMessage Snackbar message for timeouts
 * @param {Function} dispatch The Redux dispatch
 * @returns {Object} The updated status code, title, and description
 */
export function captureTimedOutRequest(
  error, statusCode, title, desc, timeoutErrorMessage, dispatch
) {
  let newStatusCode = statusCode;
  let newTitle = title;
  let newDesc = desc;
  switch (error.code) {
    case 'SOMETHING':
    case 'ECONNABORTED': {
      dispatch(pushSnackbarContent(timeoutErrorMessage));
      break;
    }
    default: {
      newStatusCode = 600;
      newTitle = messageText.error;
      newDesc = 'Internal Error';
      console.log(error);
      dispatch(updateErrorText({ errCode: newStatusCode, errDesc: newDesc, errTitle: newTitle }));
    }
  }
  return { statusCode: newStatusCode, title: newTitle, desc: newDesc };
}

/**
 * Dispatches the appropriate error dialog text based on the HTTP status,
 * including the special "create profile" 400 case.
 * @param {Object} response The Axios error response
 * @param {number} statusCode The status code to report
 * @param {string} msg The message to report
 * @param {Function} dispatch The Redux dispatch
 * @returns {void}
 */
export function captureApiError(response, statusCode, msg, dispatch) {
  const newStatusCode = statusCode;
  const newMsg = msg;
  switch (response.status) {
    case 404:
    case 500:
    case 503:
    case 401:
    case 403:
      dispatch(updateErrorText({
        errCode: newStatusCode,
        errDesc: messageText.appErrorDescription,
        errTitle: messageText.error
      }));
      break;
    case 504:
      dispatch(updateErrorText({
        errCode: newStatusCode,
        errDesc: messageText.gatewayTimeoutDescription,
        errTitle: messageText.error
      }));
      break;
    case 400:
      if (newMsg === 'User profile does not exist') {
        dispatch(updateErrorText({
          errCode: newStatusCode,
          errDesc: messageText.createProfileDesc,
          errTitle: messageText.createProfileTitle
        }));
      } else {
        dispatch(updateErrorText({
          errCode: newStatusCode,
          errDesc: newMsg,
          errTitle: messageText.error
        }));
      }
      break;
    default:
      console.log('API Error default:', response);
      dispatch(updateErrorText({
        errCode: newStatusCode,
        errDesc: newMsg,
        errTitle: messageText.error
      }));
  }
}

/**
 * Returns whether the error is a session-expired cancellation.
 * @param {Object} error The error to inspect
 * @returns {boolean} True when the session expired
 */
export function checkIfSessionExpired(error) {
  return error instanceof Axios.Cancel && error.message === 'Session Expired';
}

/**
 * Handles network vs. timed-out errors: reports API errors from a response,
 * or delegates to the timed-out handler otherwise.
 * @param {Object} error The Axios error
 * @param {number} statusCode The current status code
 * @param {string} msg The current message
 * @param {Object} responseData The current response data
 * @param {string} timeoutErrMsg Snackbar message for timeouts
 * @param {Function} dispatch The Redux dispatch
 * @returns {Object} The updated status code, message, and response data
 */
export function checkForNetworkAndTimedoutError(
  error, statusCode, msg, responseData, timeoutErrMsg, dispatch
) {
  const { response } = error;
  const { data, statusText } = response || {};
  let newStatusCode = statusCode;
  let newMsg = msg;
  let newResponseData = responseData;
  if (response && response instanceof Object) {
    newStatusCode = response.status;
    newMsg = getMessageFromApi(data, statusText);
    newResponseData = data;
    captureApiError(response, newStatusCode, newMsg, dispatch);
  } else {
    const title = '';
    const desc = '';
    try {
      const timedOutResult = captureTimedOutRequest(
        error, newStatusCode, title, desc, timeoutErrMsg, dispatch
      );
      newStatusCode = timedOutResult.statusCode;
      newMsg = error?.message || 'Network Error';
    } catch (err) {
      console.log('Unexpected Error:', err);
    }
  }
  return { statusCode: newStatusCode, msg: newMsg, responseData: newResponseData };
}

/**
 * Decrements the pending cancellable count and clears the cancel source and
 * spinner when no cancellable requests remain.
 * @param {boolean} cancellable Whether the completed request was cancellable
 * @param {Function} dispatch The Redux dispatch
 * @returns {void}
 */
function callResetCancelToken(cancellable, dispatch) {
  if (cancellable) {
    if (cancellableRequestInstance.pendingCancellableRequests) {
      cancellableRequestInstance.pendingCancellableRequests -= 1;
    }
    if (cancellableRequestInstance.pendingCancellableRequests === 0) {
      cancellableRequestInstance.source = null;
      dispatch(setCancellableSpinner(false));
    }
  }
}

/**
 * Registers a cancellable request: increments the pending count, shows the
 * cancellable spinner, and stores the cancel source if not already set.
 * @param {Object} source The Axios cancel-token source
 * @param {Function} dispatch The Redux dispatch
 * @returns {void}
 */
function checkForCancellableInstance(source, dispatch) {
  cancellableRequestInstance.pendingCancellableRequests += 1;
  dispatch(setCancellableSpinner(true));
  if (!cancellableRequestInstance.source) {
    cancellableRequestInstance.source = source;
  }
}

/**
 * Returns whether the error is a user-initiated cancellation.
 * @param {Object} error The error to inspect
 * @returns {boolean} True when cancelled by the user
 */
export function checkIfCancelledByUser(error) {
  return error instanceof Axios.Cancel && error.message === 'Cancelled by user';
}

/**
 * Performs an API request: resolves the endpoint URL, dispatches the call,
 * and normalizes the success payload or throws a structured error.
 * @param {Object} options Request options (api/url, method, data, flags)
 * @param {Function} dispatch The Redux dispatch
 * @returns {Promise<Object>} The normalized success response
 */
export default async function request(options, dispatch) {
  const source = Axios.CancelToken.source();
  if (options.cancellable) {
    checkForCancellableInstance(source, dispatch);
  }

  const resetCancelToken = (cancellable) => {
    callResetCancelToken(cancellable, dispatch);
  };
  let endpointUrl = '';
  let timeoutErrMsg = '';
  if (options.url) {
    endpointUrl = options.url;
  } else {
    const {
      url, timeoutErrorMessage
    } = getEndpointURL(options.api, options.routeParams);
    endpointUrl = url;
    timeoutErrMsg = timeoutErrorMessage;
  }
  return fetch({ ...options, url: endpointUrl, source: cancellableRequestInstance.source })
    .then((response) => {
      const { statusText, status } = response || {};
      let { data } = response || {};
      if (Array.isArray(data)) {
        data = {
          list: data
        };
      }
      return {
        success: true,
        message: statusText,
        statusCode: status,
        data
      };
    })
    .catch((error = {}) => {
      let msg;
      let statusCode;
      let responseData;
      if (checkIfCancelledByUser(error)) {
        throw new Error(JSON.stringify({
          success: false,
          statusCode: 'ABORTED_BY_USER',
          message: error.message
        }));
      }

      if (!options.suppressErrorDialog && error.code !== 'ECONNABORTED') {
        dispatch(setRequestError(true));
      }

      if (checkIfSessionExpired(error)) {
        dispatch(setSessionTimeout({
          errCode: 419,
          errDesc: messageText.sessionTimeoutDesc,
          errTitle: messageText.sessionTimeout,
          errSessionTimeout: true
        }));
      } else {
        ({ statusCode, msg, responseData } = checkForNetworkAndTimedoutError(
          error,
          statusCode,
          msg,
          responseData,
          timeoutErrMsg,
          dispatch
        ));
      }
      throw new Error(JSON.stringify({
        success: false,
        statusCode,
        message: msg,
        data: responseData
      }));
    }).finally(() => {
      resetCancelToken(options.cancellable);
    });
}
